import { createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { onFileSave, onRequestError } from 'services/handlers';
import { useUserActiveRoleIsGlobal } from 'services/userRoleService';

import { SiteFinanceApi, StudySiteApi } from '../../../../api';
import useSessionStorage from '../../../../common/hooks/useSessionStorage';
import { EXPORT_FAILED } from '../../../../constants/notificationMessages';

import {
  REVENUE_DATE,
  SITE_PAYMENT_DATE
} from './SiteFinanceHeader/SiteFinanceFilters/DateTypeSelector/DateTypeSelectorConstants';
import { filterSsuByStudyIdsAndSiteIds } from './SiteFinanceServices';

export const SiteFinanceFilterContext = createContext(null);
export const SiteFinanceTableContext = createContext(null);

export const SiteFinanceProvider = ({ children }) => {
  const firstLoading = useRef(true);
  const firstSSULoading = useRef(true);
  const [savedFilters, setSavedFilters] = useSessionStorage('SITE_FINANCE_FILTERS', {});
  const [allStudiesFms, setAllStudiesFms] = useState(false);
  const [lifetimeData, setLifetimeData] = useState({
    totalEarnedSiteAmount: 0,
    totalPaymentLifetime: 0,
    totalWithheldFromPayment: 0
  });
  const userActiveRoleIsGlobal = useUserActiveRoleIsGlobal();

  const emptyFilters = useMemo(
    () => ({
      studyName: null,
      siteName: null,
      siteId: null,
      studyId: null,
      finStudyType: null,
      projectCode: null,
      startDate: moment()
        .subtract(1, 'year')
        .set({ hour: 0, minute: 0, second: 0, millisecond: 1 }),
      endDate: moment().set({ hour: 23, minute: 59, second: 59, millisecond: 0 }),
      filterDateType: REVENUE_DATE,
      isInvoiced: true,
      isPaid: true,
      isAll: true
    }),
    []
  );

  const initialFilterData = useMemo(() => {
    return isEmpty(savedFilters)
      ? emptyFilters
      : {
          ...savedFilters,
          startDate: moment(savedFilters.startDate),
          endDate: moment(savedFilters.endDate)
        };
  }, [emptyFilters, savedFilters]);

  const [filterData, setFilterData] = useState(initialFilterData);
  const [ssus, setSsus] = useState([]);
  const [tableData, setTableData] = useState([]);

  const setFewFilterProperty = useCallback(
    filters => {
      setFilterData(prevFilters => ({
        ...prevFilters,
        ...filters
      }));
    },
    [setFilterData]
  );

  const prepareSelectedFiltersRequest = useCallback(() => {
    const studyIds = filterData.studyId ? [filterData.studyId] : [];
    const siteIds = filterData.siteId ? [filterData.siteId] : [];
    const filteredSsus = filterSsuByStudyIdsAndSiteIds(ssus, studyIds, siteIds);
    const ssuIds = filteredSsus.map(ssu => ssu.uniqueIdentifier);
    if (filterData.isAll || (!filterData.isInvoiced && !filterData.isPaid && !filterData.isAll)) {
      setFewFilterProperty({ isAll: true, isInvoiced: true, isPaid: true });
    }
    const isAllStudiesFms = filteredSsus?.every(ssu => ssu?.study?.finStudyType === 'FMS');
    setAllStudiesFms(isAllStudiesFms);
    const eventStatusFilters = filterData.isAll
      ? { isInvoiced: false, isPaid: false }
      : { isInvoiced: filterData.isInvoiced, isPaid: filterData.isPaid };
    return {
      startDate: filterData.startDate,
      endDate: filterData.endDate,
      filterDateType: filterData.filterDateType,
      studyIds,
      siteIds,
      ssuIds,
      ...eventStatusFilters
    };
  }, [filterData, setFewFilterProperty, ssus]);

  const updatePage = useCallback(() => {
    const filters = prepareSelectedFiltersRequest();
    firstLoading.current = false;
    SiteFinanceApi.getSiteFinanceEvents(filters).then(({ data }) => {
      setTableData(data);
      setSavedFilters(filterData);
    }, onRequestError);
    SiteFinanceApi.getLifetimeAmounts({
      studyIds: filters.studyIds,
      siteIds: filters.siteIds,
      ssuIds: filters.ssuIds
    }).then(({ data }) => {
      setLifetimeData(data);
    }, onRequestError);
  }, [filterData, prepareSelectedFiltersRequest, setSavedFilters]);

  const resolveSiteFinanceExportPromise = useCallback(() => {
    if (filterData?.filterDateType === REVENUE_DATE) {
      return SiteFinanceApi.exportSiteFinanceRevenueReport(prepareSelectedFiltersRequest());
    } else if (filterData?.filterDateType === SITE_PAYMENT_DATE) {
      return SiteFinanceApi.exportSiteFinancePaymentDetailsReport(prepareSelectedFiltersRequest());
    } else {
      throw new Error(`Date Type is required but is: ${filterData?.filterDateType}`);
    }
  }, [filterData?.filterDateType, prepareSelectedFiltersRequest]);

  const exportSiteFinanceReport = useCallback(() => {
    resolveSiteFinanceExportPromise()
      .then(onFileSave)
      .catch(err => onRequestError(err, { customMessage: EXPORT_FAILED }));
  }, [resolveSiteFinanceExportPromise]);

  const isSiteFinanceExportButtonDisabled = useCallback(() => {
    return !filterData?.filterDateType || isEmpty(tableData);
  }, [filterData.filterDateType, tableData]);

  const resetFilters = useCallback(() => {
    setFilterData(emptyFilters);
  }, [emptyFilters]);

  const filterContextValue = useMemo(
    () => ({
      filterData,
      setFewFilterProperty,
      updatePage,
      exportSiteFinanceReport,
      isSiteFinanceExportButtonDisabled,
      resetFilters,
      ssus
    }),
    [
      filterData,
      setFewFilterProperty,
      updatePage,
      exportSiteFinanceReport,
      isSiteFinanceExportButtonDisabled,
      resetFilters,
      ssus
    ]
  );

  const tableContextValue = useMemo(
    () => ({
      tableData,
      allStudiesFms,
      userActiveRoleIsGlobal,
      lifetimeData,
      firstLoading: firstLoading.current
    }),
    [allStudiesFms, lifetimeData, tableData, userActiveRoleIsGlobal]
  );

  useEffect(() => {
    if (!isEmpty(savedFilters) && !isEmpty(ssus) && firstLoading.current) {
      firstLoading.current = false;
      updatePage();
    }
  }, [savedFilters, ssus, updatePage]);

  useEffect(() => {
    if (firstSSULoading.current) {
      firstSSULoading.current = false;
      StudySiteApi.getStudySitesWithFinAccess()
        .then(({ data }) => {
          setSsus(data);
          const uniqueSites = [...new Set(data.map(ssu => ssu.site.uniqueIdentifier))];
          if (isEmpty(savedFilters) && uniqueSites.length === 1) {
            setFewFilterProperty({
              siteId: uniqueSites[0],
              siteName: data.map(ssu => ssu.site).filter(site => site.uniqueIdentifier === uniqueSites[0])[0].siteName
            });
          }
          const availableStudiesIncludeSavedStudy = data.some(
            ssu => ssu.study.uniqueIdentifier === savedFilters.studyId
          );
          const availableSitesIncludeSavedSite = data.some(ssu => ssu.site.uniqueIdentifier === savedFilters.siteId);

          if (
            (savedFilters.studyId && !availableStudiesIncludeSavedStudy) ||
            (savedFilters.siteId && !availableSitesIncludeSavedSite)
          ) {
            setFewFilterProperty({ studyId: null, siteId: null, studyName: null, siteName: null });
            setSavedFilters({});
          }
        })
        .catch(e => onRequestError(e));
    }
  }, [savedFilters, setFewFilterProperty, setSavedFilters, ssus]);

  return (
    <SiteFinanceFilterContext.Provider value={filterContextValue}>
      <SiteFinanceTableContext.Provider value={tableContextValue}>{children}</SiteFinanceTableContext.Provider>
    </SiteFinanceFilterContext.Provider>
  );
};

export const withSiteFinanceContext = Component => props => {
  return (
    <SiteFinanceProvider>
      <Component {...props} />
    </SiteFinanceProvider>
  );
};
